import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useShop } from '../contexts/ShopContext';
import { UserRole } from '../types';

interface RouteGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  roles?: UserRole[];
  allowAuthenticated?: boolean;
  requireShop?: boolean;
}

/**
 * RouteGuard component to handle route access based on user authentication and role
 * @param children - The route component to render if access is allowed
 * @param requireAuth - Whether authentication is required for this route
 * @param roles - Specific roles allowed to access this route
 * @param allowAuthenticated - Whether to allow authenticated users to access this route
 * @param requireShop - Whether a shop is required for this route
 */
const RouteGuard: React.FC<RouteGuardProps> = ({
  children,
  requireAuth = false,
  roles = [],
  allowAuthenticated = false,
  requireShop = false,
}) => {
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const shopContext = useShop();
  const location = useLocation();

  const isLoading = authLoading || (requireShop && shopContext?.isLoadingShop);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (requireShop && !shopContext.selectedShop && 
      !shopContext.isLoadingShop && location.pathname !== '/shops' && 
      location.pathname !== '/mechanix-hub') {
    console.log('RouteGuard: No shop selected, redirecting to /shops');
    return <Navigate to="/shops" state={{ from: location, message: "Please select a shop first." }} replace />;
  }

  if (requireShop && shopContext.shopError && location.pathname !== '/shops') {
    console.error("Shop Error in RouteGuard:", shopContext.shopError);
    return <Navigate to="/shops" state={{ from: location, error: shopContext.shopError }} replace />;
  }

  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (isAuthenticated && !requireAuth && !roles.length && !allowAuthenticated) {
    if (user?.role === UserRole.SUPER_ADMIN || user?.role === UserRole.ADMIN) {
      return <Navigate to="/admin/appointments" replace />;
    } else if (user?.role === UserRole.MECHANIC) {
      return <Navigate to="/mechanic/schedule" replace />;
    }
    return <Navigate to="/" replace />;
  }

  if (roles.length > 0 && user && !roles.includes(user.role)) {
    if (user.role === UserRole.SUPER_ADMIN || user.role === UserRole.ADMIN) {
      return <Navigate to="/admin/appointments" replace />;
    } else if (user.role === UserRole.MECHANIC) {
      return <Navigate to="/mechanic/schedule" replace />;
    } else {
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
};

export default RouteGuard; 